import { useEffect, useState, useRef, useCallback } from "react";
import { fetchDisputeMessages, postDisputeMessage } from "@/lib/api";
import { useWs } from "@/context/WsContext";
import { useAuth } from "@/context/AuthContext";
import { Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * Real-time chat thread for a dispute.
 * Backend broadcasts `{type: "dispute_message", message: {...}}` over WebSocket.
 */
export default function DisputeChat({ dispute, testIdPrefix = "disp-chat" }) {
  const { user } = useAuth();
  const { subscribe } = useWs();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const scrollDown = useCallback(() => {
    setTimeout(() => { const box = bottomRef.current?.parentElement; if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" }); }, 60);
  }, []);

  // Initial load
  useEffect(() => {
    if (!dispute?.dispute_id) return;
    fetchDisputeMessages(dispute.dispute_id)
      .then((data) => { setMsgs(data); scrollDown(); })
      .catch(() => setMsgs([]));
  }, [dispute?.dispute_id, scrollDown]);

  // Live updates
  useEffect(() => {
    if (!dispute?.dispute_id) return;
    return subscribe((evt) => {
      if (evt?.type === "dispute_message" && evt.message?.dispute_id === dispute.dispute_id) {
        setMsgs((prev) => {
          if (prev.some((m) => m.message_id === evt.message.message_id)) return prev;
          return [...prev, evt.message];
        });
        scrollDown();
      }
    });
  }, [dispute?.dispute_id, subscribe, scrollDown]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const m = await postDisputeMessage(dispute.dispute_id, t);
      setMsgs((prev) => prev.some((x) => x.message_id === m.message_id) ? prev : [...prev, m]);
      setText("");
      scrollDown();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Message failed");
    } finally { setSending(false); }
  };

  const myRole = user?.is_admin ? "admin" : (user?.user_id === dispute.buyer_id ? "buyer" : "seller");
  const isClosed = dispute.status !== "open";
  const canSend = user && (myRole === "admin" || !isClosed);

  return (
    <div className="border-t border-foreground/10 pt-3 mt-3">
      <div className="overline text-xs mb-2 flex items-center gap-1.5">
        <ShieldAlert className="w-3.5 h-3.5"/> ◆ DISPUTE THREAD
        <span data-testid={`${testIdPrefix}-count`} className="ml-auto opacity-60">{msgs.length} message{msgs.length === 1 ? "" : "s"}</span>
      </div>

      <div
        data-testid={`${testIdPrefix}-list`}
        className="max-h-72 overflow-y-auto space-y-2 border border-foreground/10 bg-muted/30 p-3"
      >
        {msgs.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-4">No messages yet. Start the conversation.</div>
        )}
        {msgs.map((m) => {
          const mine = m.sender_id === user?.user_id;
          return (
            <div
              key={m.message_id}
              data-testid={`${testIdPrefix}-msg-${m.message_id}`}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
            >
              <div className={`max-w-[85%] px-3 py-2 border ${mine ? "bg-primary text-primary-foreground border-primary" : "bg-background border-foreground/15"}`}>
                <div className={`mono text-[10px] mb-0.5 flex items-center gap-1.5 ${mine ? "opacity-80" : "text-muted-foreground"}`}>
                  <span className="uppercase font-semibold">{m.sender_role}</span>
                  <span>·</span>
                  <span>{m.sender_name}</span>
                  <span className="ml-auto opacity-70">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {canSend ? (
        <div className="flex gap-2 mt-2">
          <input
            data-testid={`${testIdPrefix}-input`}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Sending as ${myRole}…`}
            className="flex-1 border border-foreground/20 px-3 py-2 rounded-none text-sm bg-background focus:outline-none focus:border-primary"
            disabled={sending}
          />
          <button
            data-testid={`${testIdPrefix}-send`}
            onClick={send}
            disabled={!text.trim() || sending}
            className="bg-foreground text-background px-4 mono text-xs hover:bg-foreground/90 disabled:opacity-40 flex items-center gap-2"
          >
            <Send className="w-3.5 h-3.5"/> SEND
          </button>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic mt-2 mono">Dispute is closed. Only admin can comment.</div>
      )}
    </div>
  );
}
